// uuid v7 minting for the browser (spec 06 §9.2, spec 16 §5).
//
// WHY v7 AND NOT `crypto.randomUUID()`. `randomUUID` mints v4 — 122 random bits
// with no time component. That is fine for a request the browser makes and
// forgets, which is why `lib/request.ts` uses it for the office screens. It is
// NOT fine for the FLOOR OUTBOX: §9.2 requires "uuid v7 minted at tap time,
// persisted in the PWA outbox", and the v7 timestamp prefix is load-bearing —
// it makes the durable queue's primary key monotonically ordered by the instant
// the operator's finger hit the glass, so a queue restored from IndexedDB after
// a mid-drain kill replays in TAP order even if the insertion metadata is lost.
//
// The layout is RFC 9562 §5.7: 48-bit big-endian Unix-ms timestamp, version 7,
// variant 10, 74 random bits. Two keys minted inside the same millisecond differ
// in their random tail, so uniqueness never depends on the clock's resolution.
//
// RESERVED NAMESPACES (§9.2 fix round). Module 06 refuses client keys shaped
// like its own deterministic internal keys (`cycle-count:`, `adjustment-request:`).
// A uuid v7 is 36 hex-and-dash characters and can never collide with either
// prefix, so an honest client that mints keys HERE is structurally safe — which
// is exactly why the outbox has no allow-list of its own to keep in step.

const HEX: readonly string[] = Array.from({ length: 256 }, (_, i) =>
  i.toString(16).padStart(2, "0"),
);

/**
 * Random bytes from the platform CSPRNG. `crypto.getRandomValues` is present in
 * every browser the PWA supports and in jsdom/node ≥ 18; there is deliberately
 * NO `Math.random` fallback — a predictable idempotency key would let one
 * device's replay be answered with another's mutation.
 */
function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * A uuid v7 for the instant `now` (defaults to `Date.now()`).
 *
 * Injectable clock so the outbox tests can pin the timestamp prefix and assert
 * that a key is minted ONCE, at tap time — never re-derived on a retry.
 */
export function uuidV7(now: number = Date.now()): string {
  const ms = Math.max(0, Math.floor(now));
  const bytes = new Uint8Array(16);

  // 48-bit big-endian timestamp. Split so the shift never leaves the 32-bit
  // range JS bitwise operators live in (a 2^40 ms epoch would silently wrap).
  const high = Math.floor(ms / 0x1_0000_0000); // top 16 bits
  const low = ms % 0x1_0000_0000; // bottom 32 bits
  bytes[0] = (high >>> 8) & 0xff;
  bytes[1] = high & 0xff;
  bytes[2] = (low >>> 24) & 0xff;
  bytes[3] = (low >>> 16) & 0xff;
  bytes[4] = (low >>> 8) & 0xff;
  bytes[5] = low & 0xff;

  const rand = randomBytes(10);
  bytes.set(rand, 6);

  // version 7 in the high nibble of byte 6; RFC 4122 variant in byte 8.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const h = (index: number): string => HEX[bytes[index] ?? 0] ?? "00";
  return (
    `${h(0)}${h(1)}${h(2)}${h(3)}-${h(4)}${h(5)}-${h(6)}${h(7)}-${h(8)}${h(9)}-` +
    `${h(10)}${h(11)}${h(12)}${h(13)}${h(14)}${h(15)}`
  );
}

/** True for a well-formed uuid v7 (version nibble 7, RFC 4122 variant). */
export function isUuidV7(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(value);
}

/** The millisecond the key was minted, read back off a v7's timestamp prefix. */
export function uuidV7Timestamp(value: string): number | undefined {
  if (!isUuidV7(value)) return undefined;
  const hex = value.slice(0, 8) + value.slice(9, 13);
  return Number.parseInt(hex, 16);
}
